import { Box, Button, Stack, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { radius } from "src/design/tokens";

export interface BulkActionBarProps {
  /** Selected rows; the bar shows while this is above zero. */
  count: number;
  /** Action buttons for the selection. */
  actions?: ReactNode;
  onClear: () => void;
  /** The outcome of the last bulk action, announced politely ("4 deactivated, 1 skipped"). */
  result?: ReactNode;
  /**
   * Where focus goes after Clear, since the bar (and its focused Clear button) unmounts. Usually
   * the table's select-all checkbox or the table itself.
   */
  returnFocusTo?: () => HTMLElement | null | undefined;
}

/** "{count} selected", the actions for the selection and Clear, with results announced politely. */
export const BulkActionBar = ({
  count,
  actions,
  onClear,
  result,
  returnFocusTo,
}: BulkActionBarProps) => {
  const { t } = useTranslation();
  const selectedText = t("bulk.selected", { count });

  return (
    <Box>
      {count > 0 ? (
        <Stack
          component="section"
          aria-label={t("bulk.label")}
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          spacing={1.5}
          data-testid="bulk-action-bar"
          sx={(theme) => ({
            px: 2,
            py: 1.25,
            mb: 1.5,
            borderRadius: `${radius.panel}px`,
            bgcolor: theme.palette.primary.light,
            color: theme.palette.primaryInk,
          })}
        >
          <Typography component="p" sx={{ m: 0, fontWeight: 800 }}>
            {selectedText}
          </Typography>
          {actions ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {actions}
            </Stack>
          ) : null}
          <Button
            onClick={() => {
              onClear();
              returnFocusTo?.()?.focus();
            }}
            size="small"
            sx={{ ml: "auto", color: "inherit", fontWeight: 700 }}
          >
            {t("bulk.clear")}
          </Button>
        </Stack>
      ) : null}
      {/* Mounted at all times so screen readers hear each change. */}
      <Box
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={result ? { mb: 1.5, fontSize: 14 } : visuallyHidden}
      >
        {result ?? (count > 0 ? selectedText : "")}
      </Box>
    </Box>
  );
};

export default BulkActionBar;
